import { Loader2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Button } from "@akuna/ui";
import { DocumentPreview } from "@/components/print/DocumentPreview";
import { cn } from "@/lib/utils";
import { usePrescriptionDraftPreview } from "./PrescriptionDraftContext";
import { ViaTabs } from "./PrescriptionDraftPreviewCard";

export type MobileView = "edit" | "sheet";

/** "Editar | Ver folha" switch shown at the top of the mobile prescription tab. */
export function MobileViewToggle({ value, onChange }: { value: MobileView; onChange: (view: MobileView) => void }) {
  const { t } = useTranslation("prescriptions");
  return (
    <div role="tablist" className="grid h-[38px] grid-cols-2 gap-1 rounded-xl bg-secondary p-1">
      {(["edit", "sheet"] as const).map((view) => (
        <button
          key={view}
          type="button"
          role="tab"
          aria-selected={value === view}
          onClick={() => onChange(view)}
          className={cn(
            "rounded-[9px] text-[13.5px] font-semibold focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
            value === view ? "bg-card shadow-sm" : "text-muted-foreground",
          )}
        >
          {view === "edit" ? t("editor.mobile.edit") : t("editor.mobile.sheet")}
        </button>
      ))}
    </div>
  );
}

/** The real document (same HTML the printer uses), fit to the phone width. */
export function MobileSheetView() {
  const { t } = useTranslation("prescriptions");
  const { html, viaNames, activeVia, setActiveVia } = usePrescriptionDraftPreview();
  return (
    <div className="flex flex-col gap-3">
      <ViaTabs names={viaNames} active={activeVia} onChange={setActiveVia} />
      <div className="rounded-xl bg-print-workspace py-3">
        {html ? <DocumentPreview html={html} title={t("editor.preview.title")} scale="fit" /> : null}
      </div>
      <p className="text-center text-xs text-muted-foreground">{t("editor.mobile.sheetHint")}</p>
    </div>
  );
}

/** Fixed bottom bar: stays above the app's bottom navigation. Hidden while an item card is open. */
export function MobileActionBar({
  onViewSheet,
  formId,
  submitLabel,
  pending,
  showSheetButton = true,
}: {
  onViewSheet: () => void;
  formId: string;
  submitLabel: string;
  pending: boolean;
  showSheetButton?: boolean;
}) {
  const { t } = useTranslation("prescriptions");
  return (
    <div className="sticky bottom-[var(--bottom-nav-offset)] z-10 -mx-4 mt-2 flex gap-2.5 border-t bg-card px-4 py-3">
      {showSheetButton ? (
        <Button type="button" variant="outline" className="h-[50px]" onClick={onViewSheet}>
          {t("editor.mobile.sheet")}
        </Button>
      ) : null}
      <Button type="submit" form={formId} className="h-[50px] flex-1" disabled={pending}>
        {pending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" /> : null}
        {submitLabel}
      </Button>
    </div>
  );
}
